import { useCallback, useEffect, useRef, useState } from "react";

import {
  calculateTimeFromRelativePosition,
  getClampedRelativeXFromClientX,
} from "../utils/frames";

interface UseScrubberDragProps {
  duration: number;
  onTimeChange: (time: number) => void;
}

interface UseScrubberDragReturn {
  dragTime: number | null;
  scrubberRef: React.RefObject<HTMLDivElement>;
  handleMouseDown: (event: React.MouseEvent) => void;
  handleMouseMove: (event: React.MouseEvent) => void;
  handleMouseUp: () => void;
  handleFrameMouseDown: (timestamp: number, event: React.MouseEvent) => void;
  getTimeFromPosition: (clientX: number) => number;
  getTimeFromGlobalPosition: (clientX: number) => number;
}

export const useScrubberDrag = (
  props: UseScrubberDragProps,
): UseScrubberDragReturn => {
  const { duration, onTimeChange } = props;

  const scrubberRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragTime, setDragTime] = useState<number | null>(null);

  // Calculate time based on position
  const getTimeFromPosition = useCallback(
    (clientX: number) => {
      if (!scrubberRef.current || duration === 0) return 0;

      const rect = scrubberRef.current.getBoundingClientRect();
      const relativeX = clientX - rect.left;
      return calculateTimeFromRelativePosition(relativeX, rect.width, duration);
    },
    [duration],
  );

  // Calculate time based on global position (for dragging outside container)
  const getTimeFromGlobalPosition = useCallback(
    (clientX: number) => {
      if (!scrubberRef.current || duration === 0) return 0;

      const rect = scrubberRef.current.getBoundingClientRect();
      const relativeX = getClampedRelativeXFromClientX(clientX, rect);
      return calculateTimeFromRelativePosition(relativeX, rect.width, duration);
    },
    [duration],
  );

  const handleGlobalMouseMove = useCallback(
    (event: MouseEvent) => {
      if (!isDragging) return;
      const time = getTimeFromGlobalPosition(event.clientX);
      setDragTime(time);
      onTimeChange(time);
    },
    [isDragging, getTimeFromGlobalPosition, onTimeChange],
  );

  const handleGlobalMouseUp = useCallback(() => {
    if (!isDragging) return;
    setIsDragging(false);
    setDragTime(null);

    document.removeEventListener("mousemove", handleGlobalMouseMove);
    document.removeEventListener("mouseup", handleGlobalMouseUp);
  }, [isDragging, handleGlobalMouseMove]);

  const handleMouseDown = useCallback(
    (event: React.MouseEvent) => {
      setIsDragging(true);
      const time = getTimeFromPosition(event.clientX);
      setDragTime(time);
      onTimeChange(time);

      // Add global mouse event listeners
      document.addEventListener("mousemove", handleGlobalMouseMove);
      document.addEventListener("mouseup", handleGlobalMouseUp);
    },
    [
      getTimeFromPosition,
      onTimeChange,
      handleGlobalMouseMove,
      handleGlobalMouseUp,
    ],
  );

  const handleMouseMove = useCallback(
    (event: React.MouseEvent) => {
      if (!isDragging) return;

      const time = getTimeFromPosition(event.clientX);
      setDragTime(time);
      onTimeChange(time);
    },
    [isDragging, getTimeFromPosition, onTimeChange],
  );

  const handleMouseUp = useCallback(() => {
    if (!isDragging) return;
    setIsDragging(false);
    setDragTime(null);

    document.removeEventListener("mousemove", handleGlobalMouseMove);
    document.removeEventListener("mouseup", handleGlobalMouseUp);
  }, [isDragging, handleGlobalMouseMove, handleGlobalMouseUp]);

  const handleFrameMouseDown = useCallback(
    (timestamp: number, _: React.MouseEvent) => {
      // If we're clicking on a frame, jump to that timestamp immediately
      onTimeChange(timestamp);

      // Then drag from that position
      setIsDragging(true);
      setDragTime(timestamp);

      document.addEventListener("mousemove", handleGlobalMouseMove);
      document.addEventListener("mouseup", handleGlobalMouseUp);
    },
    [onTimeChange, handleGlobalMouseMove, handleGlobalMouseUp],
  );

  useEffect(() => {
    return () => {
      document.removeEventListener("mousemove", handleGlobalMouseMove);
      document.removeEventListener("mouseup", handleGlobalMouseUp);
    };
  }, [handleGlobalMouseMove, handleGlobalMouseUp]);

  return {
    dragTime,
    scrubberRef,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
    handleFrameMouseDown,
    getTimeFromPosition,
    getTimeFromGlobalPosition,
  };
};
